import {
  BuildingLibraryIcon,
  InformationCircleIcon,
  PlusIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import useAxios from "axios-hooks";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  BusinessesResDto,
  BusinessResDto,
  CreateUserDto,
  UserResDto,
  UsersResDto,
} from "../../api/Api";
import AutocompleteInput from "../../components/input/AutoCompleteInput";
import MemberForm from "../members/form/MemberForm";
import { BusinessForm } from "./form/BusinessForm";
import PaginationButtons from "../../components/pagination/PaginationButtons";
import moment from "moment";

export function BusinessesTable(props: { data: BusinessResDto[] | undefined }) {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-auto rounded-xl shadow border border-base-300 bg-base-100">
      <table className="table table-md w-full">
        <thead className="bg-base-200 sticky top-0 z-10 text-sm">
          <tr>
            <th>#</th>
            <th>Business</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Subscription</th>
            <th>Status</th>
            <th className="text-center">Created</th>
          </tr>
        </thead>
        <tbody>
          {props?.data?.map((business, index) => (
            <tr
              key={business?._id}
              className="hover:bg-base-300 transition cursor-pointer"
              onClick={() => {
                navigate(business?.businessId + "/users");
              }}
            >
              <td>{index + 1}</td>

              <td className="font-medium whitespace-nowrap justify-center">
                <div className="flex flex-col">
                  <span>{business.businessName}</span>
                  <span className="dark:text-white/40 text-black/40 text-xs">
                    {business.city}, {business.country}
                  </span>
                </div>
              </td>

              <td className="text-base-content/70 text-sm whitespace-nowrap">
                {business.email}
              </td>

              <td className="text-sm text-base-content/70 whitespace-nowrap">
                {business.phoneNumber}
              </td>
              <td>
                <span className="badge ">{business?.subscription?.status}</span>
              </td>

              <td className="text-sm">
                {business.subscription?.status ? (
                  <div className="flex flex-col text-xs gap-[2px] leading-tight">
                    <span className="text-sm text-base-content/50">
                      {moment(business.subscription.startDate).format("MMM D")}{" "}
                      - {moment(business.subscription.endDate).format("MMM D")}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-base-content/60">
                        {moment(business.subscription.startDate).isAfter(
                          moment()
                        )
                          ? "Starts in:"
                          : "Expires in:"}
                      </span>
                      <span className="text-sm font-semibold">
                        {Math.floor(
                          moment
                            .duration(
                              moment(
                                moment(business.subscription.startDate).isAfter(
                                  moment()
                                )
                                  ? business.subscription.startDate
                                  : business.subscription.endDate
                              ).diff(moment())
                            )
                            .asDays()
                        )}{" "}
                        days
                      </span>
                    </div>
                    <span className="text-sm text-base-content/50">
                      {business.subscription.studentQuantity} students
                    </span>
                  </div>
                ) : (
                  <span className="badge badge-outline text-sm text-base-content/60">
                    No subscription
                  </span>
                )}
              </td>

              <td className="text-center text-xs text-base-content/60">
                {moment(business.createdAt).format("DD MMM | YYYY")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Businesses() {
  const { setTitleComponent } = useOutletContext() as any;
  const navigate = useNavigate();

  const [searchString, setSearchString] = useState(undefined as any);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState("desc");

  const [{ data, loading, error }, getMembers] = useAxios<BusinessesResDto>({
    url: "/businesses",
    params: {
      search: searchString,

      page: page,
      limit: pageSize,
    },
  });

  const [
    {
      data: createMemberData,
      loading: createMemberLoading,
      error: createMemberError,
    },
    getBusinesses,
  ] = useAxios<UserResDto, CreateUserDto>(
    {
      url: "/businesses",
      method: "POST",
    },
    {
      manual: true,
    }
  );

  useEffect(() => {
    setTitleComponent(
      <div className="flex items-center justify-center gap-2">
        <BuildingLibraryIcon className="h-8 w-8" />
        Organizations
      </div>
    );
    if (searchString !== undefined) {
      getMembers();
    }
  }, [searchString]);

  return (
    <div className="flex flex-col gap-2 p-5">
      <div className="flex">
        <div className="mb-2">
          <AutocompleteInput
            onComplete={(input: string) => {
              setSearchString(input);
            }}
            placeHolder="Search..."
            className="bg-base-200"
          ></AutocompleteInput>
        </div>
        <div className="ml-auto">
          <button
            className="btn btn-sm"
            onClick={() => {
              (
                document?.getElementById("new_member_modal") as any
              )?.showModal();
            }}
          >
            <PlusIcon className="h-4"></PlusIcon>
            New Organization
          </button>
        </div>
      </div>
      {!loading ? (
        <div className="flex flex-col gap-2">
          <BusinessesTable data={data?.docs}></BusinessesTable>
          <PaginationButtons
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            pageCount={data?.totalPages}
          />
        </div>
      ) : (
        <div className="h-[20vh] w-full flex items-center justify-center">
          <span className="loading loading-spinner loading-md"></span>
        </div>
      )}

      <dialog id="new_member_modal" className="modal">
        <div className="modal-box">
          <div className="flex">
            <h3 className="font-bold text-lg pb-2">Create Organization</h3>
            <button
              className="btn ml-auto btn-sm btn-circle"
              onClick={() => {
                (document?.getElementById("new_member_modal") as any)?.close();
              }}
            >
              <XMarkIcon className="h-4 w-4"></XMarkIcon>
            </button>
          </div>
          <div>
            <BusinessForm
              onFinish={(business: any) => {
                (document?.getElementById("new_member_modal") as any)?.close();
                navigate(business?.businessId + "/users");
                // getMembers();
              }}
            ></BusinessForm>
          </div>
        </div>
      </dialog>
    </div>
  );
}
